import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Target, Activity, RefreshCw } from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { analysisService } from '../services/api';
import { formatCurrency, formatPercent } from '../utils/helpers';

const PortfolioPriorityPage = () => {
  const [portfolioData, setPortfolioData] = useState([]);
  const [tradingPlanData, setTradingPlanData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setIsLoading(true);

      // user_id를 localStorage에서 가져옴
      const userId = localStorage.getItem('user_id');
      console.log('✅ userId from localStorage:', userId);
      
      // user_id가 없으면 에러 처리
      if (!userId) {
        console.error('❌ user_id가 localStorage에 없습니다!');
        alert('로그인 정보가 없습니다. 다시 로그인해주세요.');
        window.location.href = '/login';
        return;
      }
      
      // 동시에 두 API 호출
      console.log('📡 API 호출 시작:', `/api/portfolio/priority/${userId}`);
      const [portfolioResponse, tradingPlanResponse] = await Promise.all([
        analysisService.getAIPortfolioPriority(userId),
        analysisService.getPortfolioTradingPlan(userId)
      ]);

      console.log('✅ 포트폴리오 우선순위 데이터:', portfolioResponse.data);
      console.log('🔍 priority API 응답 구조:', {
        keys: Object.keys(portfolioResponse.data),
        data: portfolioResponse.data.data,
        portfolio: portfolioResponse.data.portfolio,
        has_data: !!portfolioResponse.data.data,
        data_length: portfolioResponse.data.data?.length
      });
      
      console.log('✅ 매매 계획 데이터:', tradingPlanResponse.data);
      console.log('🔍 trading-plan API 응답 구조:', {
        keys: Object.keys(tradingPlanResponse.data),
        data: tradingPlanResponse.data.data,
        portfolio: tradingPlanResponse.data.portfolio,
        has_data: !!tradingPlanResponse.data.data,
        data_length: tradingPlanResponse.data.data?.length
      });

      // 응답 구조에 맞게 데이터 추출
      const priorityData = portfolioResponse.data.data || portfolioResponse.data.portfolio || [];
      const tradingData = tradingPlanResponse.data.data || tradingPlanResponse.data.portfolio || [];
      
      console.log('📦 추출된 priorityData:', priorityData);
      console.log('📦 priorityData.length:', priorityData.length);
      console.log('📦 추출된 tradingData:', tradingData);
      console.log('📦 tradingData.length:', tradingData.length);

      setPortfolioData(priorityData);
      setTradingPlanData(tradingData);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('❌ 포트폴리오 데이터 로드 오류:', error);
      console.error('에러 상세:', error.response?.data);
      alert('포트폴리오 데이터 로드에 실패했습니다: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const combinePortfolioData = () => {
  // 포트폴리오 우선순위와 매매 계획을 결합
    const combinedData = portfolioData.map(portfolio => {
      const tradingPlan = tradingPlanData.find(plan => 
        plan.stock_code === portfolio.stock_code
      );

      return {
        priority_rank: portfolio.priority_rank,
        stock_code: portfolio.stock_code,
        company_name: portfolio.company_name,
        sector: portfolio.sector,
        current_price: portfolio.current_price,
        target_price: portfolio.initial_target_price || portfolio.target_price,
        confidence_score: portfolio.confidence_score,
        expected_return_rate: portfolio.expected_return_rate,
        // ✅ 수정: portfolio에서 먼저 가져오고, 없으면 tradingPlan, 둘 다 없으면 0
        target_quantity: portfolio.target_quantity || tradingPlan?.target_quantity || 0,
        current_quantity: portfolio.current_quantity || tradingPlan?.current_quantity || 0,
        quantity_diff: portfolio.quantity_diff || tradingPlan?.quantity_diff || 0,
        next_action: portfolio.action_needed || tradingPlan?.next_action || '대기',
        execution_status: tradingPlan?.execution_status || 'PENDING'
      };
    });

    return combinedData.sort((a, b) => a.priority_rank - b.priority_rank);
  };

  const getActionColor = (action) => {
    switch (action) {
      case '매수': return 'text-red-600 bg-red-50';
      case '매도': return 'text-blue-600 bg-blue-50';
      case '대기': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'text-green-600 bg-green-50';
      case 'PENDING': return 'text-yellow-600 bg-yellow-50';
      case 'FAILED': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <Loading message="AI 포트폴리오 우선순위를 불러오는 중..." />
      </Layout>
    );
  }

  const combinedData = combinePortfolioData();

  return (
    <Layout>
      <div className="space-y-6">
        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              AI 포트폴리오 우선순위
            </h1>
            <p className="text-gray-600">
              AI가 분석한 포트폴리오 우선순위와 매매 계획을 확인하세요
            </p>
          </div>
          <button
            onClick={loadPortfolioData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
            새로고침
          </button>
        </div>

        {/* 요약 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">총 포트폴리오</p>
                <p className="text-2xl font-bold text-gray-900">{combinedData.length}개</p>
              </div>
              <Target className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">매수 대기</p>
                <p className="text-2xl font-bold text-red-600">
                  {combinedData.filter(item => item.next_action === '매수').length}개
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">매도 대기</p>
                <p className="text-2xl font-bold text-blue-600">
                  {combinedData.filter(item => item.next_action === '매도').length}개
                </p>
              </div>
              <Activity className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">평균 신뢰도</p>
                <p className="text-2xl font-bold text-green-600">
                  {combinedData.length > 0 
                    ? (combinedData.reduce((sum, item) => sum + item.confidence_score, 0) / combinedData.length).toFixed(1)
                    : 0
                  }점
                </p>
              </div>
              <Brain className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* 포트폴리오 테이블 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">포트폴리오 상세 현황</h2>
            {lastUpdated && (
              <p className="text-sm text-gray-500 mt-1">
                마지막 업데이트: {lastUpdated.toLocaleString()}
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    순위
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    종목정보
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    현재가
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    목표가
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    목표수량
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    현재수량
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    차이
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    다음액션
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    신뢰도
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {combinedData.map((item, index) => (
                  <tr key={item.stock_code} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {/* 순위 */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-800 text-sm font-medium">
                          {item.priority_rank}
                        </span>
                      </div>
                    </td>

                    {/* 종목정보 */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.company_name}</div>
                        <div className="text-sm text-gray-500">{item.stock_code} | {item.sector}</div>
                      </div>
                    </td>

                    {/* 현재가 */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(item.current_price)}
                      </div>
                    </td>

                    {/* 목표가 */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(item.target_price)}
                      </div>
                      <div className="text-xs text-green-600">
                        +{formatPercent(((item.target_price - item.current_price) / item.current_price) * 100)}
                      </div>
                    </td>

                    {/* 목표수량 */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {item.target_quantity.toLocaleString()}주
                      </div>
                    </td>

                    {/* 현재수량 */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {item.current_quantity.toLocaleString()}주
                      </div>
                    </td>

                    {/* 차이 */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className={`text-sm font-medium ${
                        item.quantity_diff > 0 ? 'text-red-600' : 
                        item.quantity_diff < 0 ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {item.quantity_diff > 0 ? '+' : ''}{item.quantity_diff.toLocaleString()}주
                      </div>
                    </td>

                    {/* 다음액션 */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(item.next_action)}`}>
                        {item.next_action}
                      </span>
                    </td>

                    {/* 신뢰도 */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-red-400 to-green-400 h-2 rounded-full"
                            style={{ width: `${item.confidence_score}%` }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {item.confidence_score.toFixed(1)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {combinedData.length === 0 && (
            <div className="text-center py-12">
              <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">포트폴리오 데이터 없음</h3>
              <p className="text-gray-500">
                현재 활성화된 AI 포트폴리오가 없습니다.<br />
                AI 분석이 완료되면 자동으로 표시됩니다.
              </p>
            </div>
          )}
        </div>

        {/* 추가 정보 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <Brain className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                AI 포트폴리오 우선순위 정보
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>순위는 (목표가 - 현재가) × 신뢰도로 계산된 기대수익 기준입니다</li>
                  <li>매수/매도 액션은 목표수량과 현재수량의 차이를 기반으로 결정됩니다</li>
                  <li>신뢰도는 AI 분석의 확신 정도를 나타냅니다 (0-100점)</li>
                  <li>데이터는 매일 오전 8:30, 9:10에 자동으로 업데이트됩니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PortfolioPriorityPage;
